import { Dialog } from '@components/Dialog.tsx'
import type { GeoState } from '@hooks/useGeolocation.ts'
import { DISTANCE_STOPS } from './filter.ts'
import { LocationStatus } from './LocationStatus.tsx'
import styles from './DistanceFilterDialog.module.css'

type Props = {
	open: boolean
	value: number | null
	geo: GeoState
	onRetryLocation: () => void
	onChange: (value: number | null) => void
	onClose: () => void
}

// Slider positions: index 0 is "Any" (off), the rest map to DISTANCE_STOPS.
// Even spacing so 1 mi is as easy to land on as 25.
const STOPS: (number | null)[] = [null, ...DISTANCE_STOPS]

export const DistanceFilterDialog = ({
	open,
	value,
	geo,
	onRetryLocation,
	onChange,
	onClose,
}: Props) => {
	const index = Math.max(
		0,
		STOPS.findIndex((s) => s === value),
	)
	const label = value === null ? 'Any distance' : `Within ${value} miles`

	return (
		<Dialog open={open} onClose={onClose}>
			<div className={styles.panel}>
				<div className={styles.headerRow}>
					<h2 className={styles.heading}>Distance</h2>
					<button
						type="button"
						className={styles.clear}
						onClick={() => onChange(null)}
						disabled={value === null}
					>
						Clear
					</button>
				</div>

				<p className={styles.value}>{label}</p>

				<input
					className={styles.slider}
					type="range"
					min={0}
					max={STOPS.length - 1}
					step={1}
					value={index}
					onChange={(e) =>
						onChange(STOPS[Number(e.target.value)] ?? null)
					}
					aria-label="Maximum distance"
				/>

				<div className={styles.ticks} aria-hidden="true">
					{STOPS.map((s) => (
						<span key={s ?? 'any'} className={styles.tick}>
							{s ?? 'Any'}
						</span>
					))}
				</div>

				{geo.status === 'granted' ? (
					<p className={styles.note}>Using your current location.</p>
				) : (
					<LocationStatus geo={geo} onRetry={onRetryLocation} />
				)}

				<div className={styles.actions}>
					<button
						type="button"
						className={styles.done}
						onClick={onClose}
					>
						Done
					</button>
				</div>
			</div>
		</Dialog>
	)
}
